/**
 * Projector / cast mode hook for Lumyn Live host.
 *
 * Trainer hits "Cast" → entire root element goes fullscreen, body gets a
 * data-attr that hides the sidebar/topbar via CSS, and the cursor fades out
 * after 3s of inactivity. Esc/fullscreenchange cleans up.
 *
 * Extracted verbatim from LiveHostPage.jsx during the Phase 2 refactor
 * (no behaviour change).
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const PROJECTOR_IDLE_MS = 3000;  // ms of no mouse activity → hide cursor

export function useProjectorMode() {
  const [active, setActive] = useState(false);
  const idleTimer = useRef(null);

  const exit = useCallback(async () => {
    setActive(false);
    document.body.removeAttribute('data-projector');
    document.body.removeAttribute('data-projector-idle');
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch { /* user already exited */ }
    }
    if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
  }, []);

  const enter = useCallback(async () => {
    try {
      await document.documentElement.requestFullscreen();
      setActive(true);
      document.body.setAttribute('data-projector', 'active');
    } catch {
      toast.error('Fullscreen blocked by browser. Try clicking Cast again.');
    }
  }, []);

  // Keep state in sync with the actual fullscreen state (Esc, F11, etc.)
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) exit();
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [exit]);

  // Cursor auto-hide while in projector mode
  useEffect(() => {
    if (!active) return undefined;
    const resetIdle = () => {
      document.body.removeAttribute('data-projector-idle');
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        document.body.setAttribute('data-projector-idle', 'true');
      }, PROJECTOR_IDLE_MS);
    };
    resetIdle();
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      document.body.removeAttribute('data-projector-idle');
    };
  }, [active]);

  // Cleanup on unmount
  useEffect(() => () => exit(), [exit]);

  return { active, enter, exit };
}
